import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  type OnDestroy,
} from '@angular/core';
import {
  Storage,
  UploadTask,
  UploadTaskSnapshot,
  ref,
  uploadBytesResumable,
} from '@angular/fire/storage';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  multiMap,
  safeCombineLatest,
  shareReplayHot,
} from '@principle-theorem/shared';
import { partition } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { buildUploadFilePath } from '../media-manager';
import { TrackByFunctions } from '../track-by';
import { LetDirective } from '@ngrx/component';
import { PipesModule } from '../pipes/pipes.module';
import { NgSharedModule } from '../ng-shared.module';

export interface IUploadStartedEvent {
  task: UploadTask;
  file: File;
  storagePath: string;
}

interface IFileData {
  path: string;
  file: File;
}

interface IUploadTask {
  upload: UploadTask;
  snapshot$: Observable<UploadTaskSnapshot | undefined>;
  percentage$: Observable<number | undefined>;
  fileData: IFileData;
}

function toFileExtension(extension: string): string {
  const lowercased = extension.toLowerCase();
  return lowercased.startsWith('.') ? lowercased : `.${lowercased}`;
}

@Component({
  selector: 'pt-file-upload-to-storage',
  templateUrl: './file-upload-to-storage.component.html',
  styleUrls: ['./file-upload-to-storage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressBarModule,
    LetDirective,
    PipesModule,
    NgSharedModule,
  ],
})
export class FileUploadToStorageComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _fileUpload$ = new BehaviorSubject<IFileData[]>([]);
  private _acceptedExtensions: string[] = [];
  @Input() path: string;
  @Input({ transform: (value: unknown) => coerceBooleanProperty(value) })
  omitFilePrefix = false;
  @Input({
    transform: (value: unknown) => coerceBooleanProperty(value ?? true),
  })
  multiple = true;
  @Output() uploadStarted = new EventEmitter<IUploadStartedEvent>();
  @Output() filesRejected = new EventEmitter<File[]>();
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  uploaderId: string = 'uploader-' + uuid();
  trackByTask = TrackByFunctions.index<IUploadTask>();
  tasks$: Observable<IUploadTask[]>;
  isHovering: boolean;
  acceptAttribute?: string;

  @Input()
  set acceptExtensions(extensions: string[] | undefined) {
    this._acceptedExtensions = (extensions ?? []).map(toFileExtension);
    this.acceptAttribute = this._acceptedExtensions.join(',') || undefined;
  }

  constructor(private _storage: Storage) {
    this.tasks$ = this._fileUpload$.pipe(
      multiMap((fileData) => {
        const upload = uploadBytesResumable(
          ref(this._storage, fileData.path),
          fileData.file
        );

        const percentage$ = new Observable<number>((observer) => {
          upload.on(
            'state_changed',
            (snapshot) => {
              const percentage =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              observer.next(percentage);
            },
            (error) => observer.error(error),
            () => observer.complete()
          );
        });

        const snapshot$ = new Observable<UploadTaskSnapshot>((observer) => {
          upload.on(
            'state_changed',
            (snapshot) => observer.next(snapshot),
            (error) => observer.error(error),
            () => observer.complete()
          );
        });

        return {
          upload,
          fileData,
          percentage$,
          snapshot$,
        };
      }),
      shareReplayHot(this._onDestroy$)
    );

    this.tasks$
      .pipe(
        filter((tasks) => tasks.length > 0),
        switchMap((tasks) =>
          safeCombineLatest(
            tasks.map((task) =>
              task.percentage$.pipe(filter((percentage) => percentage === 100))
            )
          )
        ),
        takeUntil(this._onDestroy$)
      )
      .subscribe(() => this._fileUpload$.next([]));

    this.tasks$.pipe(takeUntil(this._onDestroy$)).subscribe((tasks) =>
      tasks.map((task) =>
        this.uploadStarted.emit({
          task: task.upload,
          file: task.fileData.file,
          storagePath: task.fileData.path,
        })
      )
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  toggleHover(event: boolean): void {
    this.isHovering = event;
  }

  startUpload(event: Event): void {
    const element = event.target as HTMLInputElement;
    if (!element.files) {
      return;
    }
    this.fileDropped(element.files);
    this.fileInput.nativeElement.value = '';
  }

  fileDropped(fileList: FileList): void {
    const droppedFiles = Array.from(fileList);
    const selectedFiles = this.multiple
      ? droppedFiles
      : droppedFiles.slice(0, 1);
    const [acceptedFiles, rejectedFiles] = partition(selectedFiles, (file) =>
      this._isAccepted(file)
    );

    if (rejectedFiles.length) {
      this.filesRejected.emit(rejectedFiles);
    }

    if (!acceptedFiles.length) {
      return;
    }

    const files = acceptedFiles.map((file) => ({
      file,
      path: buildUploadFilePath(this.path, file.name, !this.omitFilePrefix),
    }));

    this._fileUpload$.next(files);
  }

  isActive(snapshot: UploadTaskSnapshot): boolean {
    const isRunning: boolean = snapshot.state === 'running';
    const bytesRemaining: number =
      snapshot.totalBytes - snapshot.bytesTransferred;
    return isRunning && bytesRemaining > 0;
  }

  private _isAccepted(file: File): boolean {
    if (!this._acceptedExtensions.length) {
      return true;
    }
    const fileName = file.name.toLowerCase();
    return this._acceptedExtensions.some((extension) =>
      fileName.endsWith(extension)
    );
  }
}
